import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface Props { range: 'P' | 'R' }

interface SectorWeight {
  portfolio: number;
  sector_id: string;
  cbam_weight: number;
  benchmark_blended_weight: number;
  relative_weight: number;
}
interface MasterSector { id: string; name: string; sector_group: string }
interface DailyValue { portfolio_level: number; series_type: 'portfolio' | 'benchmark'; value_date: string; value: number }
interface HoldingDaily { isin: string; value_date: string; value: number; holding_name: string | null }
interface FundLite { id: string; isin: string | null; fe_fund_name: string; fund_name: string; is_current: boolean; ocf_pct: number | null; fund_manager: string | null; fund_class: string | null }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; effective_date: string }
interface FundSectorMap { fund_id: string; sector_id: string; weighting: number }
interface FundIaMap { fund_id: string; ia_sector_id: string; weighting: number }
interface IaSector { id: string; name: string; sort_order: number | null }
interface IaDaily { ia_sector_id: string; value_date: string; value: number }

// Calendar-day periods (CBAM Perf sheet stores one row per calendar day).
const PERIODS: { label: string; days: number }[] = [
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
  { label: '1 year', days: 365 },
  { label: '3 years', days: 1095 },
];

function periodReturn(values: { value_date: string; value: number }[], days: number): number | null {
  if (values.length < 2) return null;
  const sorted = [...values].sort((a, b) => a.value_date.localeCompare(b.value_date));
  const end = sorted[sorted.length - 1];
  const startIdx = Math.max(0, sorted.length - 1 - days);
  const start = sorted[startIdx];
  if (!(start.value > 0) || !(end.value > 0)) return null;
  return (end.value / start.value - 1) * 100;
}

async function fetchAllRows<T>(table: string, select: string, eqs: [string, any][] = [], orderCol?: string): Promise<T[]> {
  const all: T[] = [];
  const pageSize = 1000;
  let from = 0;
  while (true) {
    let q: any = supabase.from(table as any).select(select);
    for (const [k, v] of eqs) q = q.eq(k, v);
    if (orderCol) q = q.order(orderCol, { ascending: true });
    q = q.range(from, from + pageSize - 1);
    const { data, error } = await q;
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...(data as T[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

const fetchAllDaily = (range: 'P' | 'R') =>
  fetchAllRows<DailyValue>('risk_daily_values', 'portfolio_level, series_type, value_date, value',
    [['portfolio_range', range]], 'value_date');

export default function PerformanceAttributionPanel({ range }: Props) {
  const [days, setDays] = useState<number>(365);
  const [netOfFees, setNetOfFees] = useState<boolean>(false);

  const { data: sectorWeights = [] } = useQuery({
    queryKey: ['attribution-sector-weights'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_portfolio_sector_weights')
        .select('portfolio, sector_id, cbam_weight, benchmark_blended_weight, relative_weight');
      if (error) throw error;
      return data as SectorWeight[];
    },
  });

  const { data: sectors = [] } = useQuery({
    queryKey: ['attribution-master-sectors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_master_sectors')
        .select('id, name, sector_group')
        .order('display_order');
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const { data: daily = [] } = useQuery({
    queryKey: ['attribution-daily', range],
    queryFn: () => fetchAllDaily(range),
  });

  const { data: holdingDaily = [] } = useQuery({
    queryKey: ['attribution-holding-daily'],
    queryFn: () => fetchAllRows<HoldingDaily>('risk_holding_daily_values', 'isin, value_date, value, holding_name', [], 'value_date'),
  });

  const { data: sectorReturns = [] } = useQuery({
    queryKey: ['attribution-sector-returns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policy_sector_index_returns' as any)
        .select('sector_id, period_days, return_pct');
      if (error) throw error;
      return (data ?? []) as unknown as { sector_id: string; period_days: number; return_pct: number }[];
    },
  });

  const { data: funds = [] } = useQuery({
    queryKey: ['attribution-funds-lite-ocf'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_cbam_funds')
        .select('id, isin, fe_fund_name, fund_name, is_current, ocf_pct, fund_manager, fund_class');
      if (error) throw error;
      return data as FundLite[];
    },
  });

  const { data: iaSectors = [] } = useQuery({
    queryKey: ['attribution-ia-sectors'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ia_sectors').select('id, name, sort_order').order('sort_order');
      if (error) throw error;
      return data as IaSector[];
    },
  });

  const { data: fundIaMaps = [] } = useQuery({
    queryKey: ['attribution-fund-ia-maps'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ba_cbam_fund_ia_sector_mappings').select('fund_id, ia_sector_id, weighting');
      if (error) throw error;
      return data as FundIaMap[];
    },
  });

  const { data: iaDaily = [] } = useQuery({
    queryKey: ['attribution-ia-daily'],
    queryFn: () => fetchAllRows<IaDaily>('risk_ia_daily_values', 'ia_sector_id, value_date, value', [], 'value_date'),
  });

  const weightsTable = range === 'P' ? 'ba_cbam_fund_weights' : 'ba_r_cbam_fund_weights';
  const { data: fundWeights = [] } = useQuery({
    queryKey: ['attribution-fund-weights', range],
    queryFn: async () => {
      const { data, error } = await supabase.from(weightsTable as any).select('fund_id, portfolio, weight_pct, effective_date');
      if (error) throw error;
      const latest = new Map<string, FundWeight>();
      for (const w of (data as any) as FundWeight[]) {
        const k = `${w.fund_id}-${w.portfolio}`;
        const prev = latest.get(k);
        if (!prev || w.effective_date > prev.effective_date) latest.set(k, w);
      }
      return Array.from(latest.values());
    },
  });

  const { data: fundSectorMaps = [] } = useQuery({
    queryKey: ['attribution-fund-sector-maps'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ba_cbam_fund_sector_mappings').select('fund_id, sector_id, weighting');
      if (error) throw error;
      return data as FundSectorMap[];
    },
  });

  const sectorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sectors) m.set(s.id, s.name);
    return m;
  }, [sectors]);

  const sectorReturnMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of sectorReturns) m.set(`${r.sector_id}-${r.period_days}`, Number(r.return_pct));
    return m;
  }, [sectorReturns]);

  const fundIsinById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of funds) if (f.is_current && f.isin) m.set(f.id, f.isin.trim().toUpperCase());
    return m;
  }, [funds]);

  const fundNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of funds) m.set(f.id, f.fe_fund_name || f.fund_name);
    return m;
  }, [funds]);

  const fundMetaById = useMemo(() => {
    const m = new Map<string, { manager: string | null; cls: string | null }>();
    for (const f of funds) m.set(f.id, { manager: f.fund_manager, cls: f.fund_class });
    return m;
  }, [funds]);

  const fundOcfById = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of funds) if (f.ocf_pct != null) m.set(f.id, Number(f.ocf_pct));
    return m;
  }, [funds]);

  const iaSectorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of iaSectors) m.set(s.id, s.name);
    return m;
  }, [iaSectors]);

  // Per-IA-sector return for chosen period (used for sleeve drilldown and benchmark sleeve return)
  const iaSectorReturnById = useMemo(() => {
    const grouped = new Map<string, { value_date: string; value: number }[]>();
    for (const r of iaDaily) {
      const arr = grouped.get(r.ia_sector_id) ?? [];
      arr.push({ value_date: r.value_date, value: Number(r.value) });
      grouped.set(r.ia_sector_id, arr);
    }
    const m = new Map<string, number>();
    for (const [id, vs] of grouped) {
      const ret = periodReturn(vs, days);
      if (ret != null) m.set(id, ret);
    }
    return m;
  }, [iaDaily, days]);

  // Portfolio-weighted annual OCF per portfolio (% per annum), then scaled to selected period
  const portfolioOcfByLevel = useMemo(() => {
    const m = new Map<number, { annualOcf: number; periodFee: number; coverage: number }>();
    const periodScale = days / 365;
    for (const level of [1, 2, 3, 4, 5]) {
      const wts = fundWeights.filter(w => w.portfolio === level && Number(w.weight_pct) > 0);
      let totalW = 0, weightedOcf = 0, coveredW = 0;
      for (const w of wts) {
        const wt = Number(w.weight_pct);
        totalW += wt;
        const ocf = fundOcfById.get(w.fund_id);
        if (ocf != null) { weightedOcf += wt * ocf; coveredW += wt; }
      }
      const annualOcf = totalW > 0 ? weightedOcf / totalW : 0;
      m.set(level, { annualOcf, periodFee: annualOcf * periodScale, coverage: totalW > 0 ? coveredW / totalW : 0 });
    }
    return m;
  }, [fundWeights, fundOcfById, days]);

  // Per-holding return for chosen period, by ISIN
  const holdingReturnByIsin = useMemo(() => {
    const grouped = new Map<string, { value_date: string; value: number }[]>();
    for (const h of holdingDaily) {
      const key = h.isin.trim().toUpperCase();
      const arr = grouped.get(key) ?? [];
      arr.push({ value_date: h.value_date, value: Number(h.value) });
      grouped.set(key, arr);
    }
    const m = new Map<string, number>();
    for (const [isin, vs] of grouped) {
      const r = periodReturn(vs, days);
      if (r != null) m.set(isin, r);
    }
    return m;
  }, [holdingDaily, days]);

  // For each portfolio, compute per-sector portfolio return = Σ (fund_share_within_sector × fund_return)
  // fund_share_within_sector = (fund_weight × sector_weighting) / total_weight_in_sector
  const sectorPortReturnByLevel = useMemo(() => {
    const out = new Map<number, Map<string, { ret: number; coverage: number }>>();
    for (const level of [1, 2, 3, 4, 5]) {
      const wts = fundWeights.filter(w => w.portfolio === level && Number(w.weight_pct) > 0);
      // accumulate per-sector contributions
      const sectorAccum = new Map<string, { weighted: number; total: number; covered: number }>();
      for (const w of wts) {
        const isin = fundIsinById.get(w.fund_id);
        const fundRet = isin ? holdingReturnByIsin.get(isin) : undefined;
        const maps = fundSectorMaps.filter(m => m.fund_id === w.fund_id);
        for (const m of maps) {
          const contribution = Number(w.weight_pct) * Number(m.weighting); // raw effective weight
          const cur = sectorAccum.get(m.sector_id) ?? { weighted: 0, total: 0, covered: 0 };
          cur.total += contribution;
          if (fundRet != null) {
            cur.weighted += contribution * fundRet;
            cur.covered += contribution;
          }
          sectorAccum.set(m.sector_id, cur);
        }
      }
      const lvlMap = new Map<string, { ret: number; coverage: number }>();
      for (const [sid, acc] of sectorAccum) {
        if (acc.covered > 0) {
          lvlMap.set(sid, { ret: acc.weighted / acc.covered, coverage: acc.covered / Math.max(acc.total, 0.0001) });
        }
      }
      out.set(level, lvlMap);
    }
    return out;
  }, [fundWeights, fundIsinById, holdingReturnByIsin, fundSectorMaps]);

  const summary = useMemo(() => {
    return [1, 2, 3, 4, 5].map(level => {
      const port = daily.filter(d => d.portfolio_level === level && d.series_type === 'portfolio');
      const bench = daily.filter(d => d.portfolio_level === level && d.series_type === 'benchmark');
      const portRet = periodReturn(port, days);
      const benchRet = periodReturn(bench, days);
      const active = portRet != null && benchRet != null ? portRet - benchRet : null;

      const wts = sectorWeights.filter(w => w.portfolio === level);
      const sectorPortRet = sectorPortReturnByLevel.get(level) ?? new Map();

      let allocationEffect: number | null = null;
      let selectionEffect: number | null = null;
      let interactionEffect: number | null = null;
      let coveredSectorWeight = 0;
      let mode: 'full-brinson' | 'allocation-only' | 'residual-only' = 'residual-only';

      if (benchRet != null && portRet != null) {
        let alloc = 0, sel = 0, inter = 0;
        let canFullDecomp = false;
        for (const w of wts) {
          const wp = Number(w.cbam_weight) / 100;
          const wb = Number(w.benchmark_blended_weight) / 100;
          const rb = sectorReturnMap.get(`${w.sector_id}-${days}`);
          const rpEntry = sectorPortRet.get(w.sector_id);
          const rp = rpEntry?.ret;

          if (rb != null) {
            // Allocation: (wp - wb) × (Rb_sector - Rb_total)
            alloc += (wp - wb) * (rb - benchRet);
            coveredSectorWeight += Math.max(wb, 0);
            if (rp != null) {
              // Selection: wb × (Rp_sector - Rb_sector)
              sel += wb * (rp - rb);
              // Interaction: (wp - wb) × (Rp_sector - Rb_sector)
              inter += (wp - wb) * (rp - rb);
              canFullDecomp = true;
            }
          }
        }

        if (canFullDecomp) {
          allocationEffect = alloc;
          selectionEffect = sel;
          interactionEffect = inter;
          mode = 'full-brinson';
        } else if (sectorReturnMap.size > 0) {
          allocationEffect = alloc;
          selectionEffect = active != null ? active - alloc : null;
          interactionEffect = null;
          mode = 'allocation-only';
        } else {
          allocationEffect = null;
          selectionEffect = active;
          mode = 'residual-only';
        }
      }

      const ocfInfo = portfolioOcfByLevel.get(level) ?? { annualOcf: 0, periodFee: 0, coverage: 0 };
      const portRetNet = portRet != null ? portRet - ocfInfo.periodFee : null;
      const activeNet = portRetNet != null && benchRet != null ? portRetNet - benchRet : null;

      return { level, portRet, portRetNet, benchRet, active, activeNet, allocationEffect, selectionEffect, interactionEffect, coveredSectorWeight, mode, annualOcf: ocfInfo.annualOcf, periodFee: ocfInfo.periodFee, ocfCoverage: ocfInfo.coverage };
    });
  }, [daily, sectorWeights, sectorReturnMap, sectorPortReturnByLevel, days, portfolioOcfByLevel]);

  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  // Sector drill-down rows
  const sectorRows = useMemo(() => {
    if (expandedLevel == null) return [];
    const benchRet = summary.find(s => s.level === expandedLevel)?.benchRet ?? 0;
    const sectorPortRet = sectorPortReturnByLevel.get(expandedLevel) ?? new Map();
    return sectorWeights
      .filter(w => w.portfolio === expandedLevel && (Number(w.cbam_weight) > 0 || Number(w.benchmark_blended_weight) > 0))
      .map(w => {
        const wp = Number(w.cbam_weight) / 100;
        const wb = Number(w.benchmark_blended_weight) / 100;
        const rb = sectorReturnMap.get(`${w.sector_id}-${days}`);
        const rpEntry = sectorPortRet.get(w.sector_id);
        const rp = rpEntry?.ret;
        const allocC = rb != null ? (wp - wb) * (rb - benchRet) : 0;
        const selC = (rb != null && rp != null) ? wb * (rp - rb) : 0;
        const interC = (rb != null && rp != null) ? (wp - wb) * (rp - rb) : 0;
        return {
          sector: sectorNameById.get(w.sector_id) ?? w.sector_id,
          wp: Number(w.cbam_weight),
          wb: Number(w.benchmark_blended_weight),
          rel: Number(w.relative_weight),
          rb, rp,
          allocC, selC, interC,
          totalC: allocC + selC + interC,
        };
      })
      .sort((a, b) => Math.abs(b.totalC) - Math.abs(a.totalC));
  }, [expandedLevel, sectorWeights, summary, sectorNameById, sectorReturnMap, sectorPortReturnByLevel, days]);

  // Top holding contributors per portfolio
  const holdingRows = useMemo(() => {
    if (expandedLevel == null) return [];
    const wts = fundWeights.filter(w => w.portfolio === expandedLevel && Number(w.weight_pct) > 0);
    return wts.map(w => {
      const isin = fundIsinById.get(w.fund_id);
      const ret = isin ? holdingReturnByIsin.get(isin) : undefined;
      const wt = Number(w.weight_pct);
      const contribution = ret != null ? (wt / 100) * ret : null;
      const meta = fundMetaById.get(w.fund_id);
      return {
        name: fundNameById.get(w.fund_id) ?? w.fund_id,
        manager: meta?.manager ?? null,
        cls: meta?.cls ?? null,
        isin: isin ?? '—',
        weight: wt,
        ret: ret ?? null,
        contribution,
        hasData: ret != null,
      };
    }).sort((a, b) => Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0));
  }, [expandedLevel, fundWeights, fundIsinById, fundNameById, fundMetaById, holdingReturnByIsin]);

  // Sleeve (IA sector) drill-down for the expanded portfolio
  // Portfolio sleeve weight = Σ over funds of (fund_weight × ia_mapping_weighting).
  // Sleeve return = IA sector index period return (used as both portfolio & benchmark sleeve return).
  const sleeveRows = useMemo(() => {
    if (expandedLevel == null) return { rows: [] as { id: string; name: string; weight: number; ret: number | null; contribution: number | null }[], totalEff: 0 };
    const wts = fundWeights.filter(w => w.portfolio === expandedLevel && Number(w.weight_pct) > 0);
    const accum = new Map<string, number>();
    let totalEff = 0;
    for (const w of wts) {
      const wt = Number(w.weight_pct);
      const maps = fundIaMaps.filter(m => m.fund_id === w.fund_id);
      for (const m of maps) {
        const c = wt * Number(m.weighting) / 100; // percentage points of portfolio
        accum.set(m.ia_sector_id, (accum.get(m.ia_sector_id) ?? 0) + c);
        totalEff += c;
      }
    }
    const rows = Array.from(accum.entries()).map(([id, w]) => {
      const ret = iaSectorReturnById.get(id) ?? null;
      return {
        id,
        name: iaSectorNameById.get(id) ?? id,
        weight: w,
        ret,
        contribution: ret != null ? (w / 100) * ret : null,
      };
    });
    rows.sort((a, b) => b.weight - a.weight);
    return { rows, totalEff };
  }, [expandedLevel, fundWeights, fundIaMaps, iaSectorReturnById, iaSectorNameById]);

  const isinsWithData = holdingReturnByIsin.size;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance Attribution — Tier 1 §12 ({range} Portfolios)</CardTitle>
        <CardDescription>
          Active return decomposed using <strong>Brinson-Fachler</strong>: Allocation = Σ (wp − wb)(Rb_s − Rb), Selection = Σ wb(Rp_s − Rb_s), Interaction = Σ (wp − wb)(Rp_s − Rb_s). Per-sector portfolio returns are derived from holding-level daily values weighted by current fund × sector mappings. {isinsWithData === 0 ? <em className="text-amber-600">No holding daily values loaded — falling back to allocation-only with sector index returns.</em> : <span className="text-muted-foreground">{isinsWithData} holdings with daily data.</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-4 mb-4 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs">Period</Label>
            <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIODS.map(p => <SelectItem key={p.days} value={String(p.days)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Fee basis</Label>
            <Select value={netOfFees ? 'net' : 'gross'} onValueChange={v => setNetOfFees(v === 'net')}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gross">Gross of fees</SelectItem>
                <SelectItem value="net">Net of OCF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground ml-auto">
            Click a portfolio row to see sector, sleeve and holding contributions.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Portfolio</TableHead>
              <TableHead className="text-right">Portfolio (%)</TableHead>
              <TableHead className="text-right">Benchmark (%)</TableHead>
              <TableHead className="text-right">Active (%)</TableHead>
              <TableHead className="text-right">OCF p.a. (%)</TableHead>
              <TableHead className="text-right">Allocation</TableHead>
              <TableHead className="text-right">Selection</TableHead>
              <TableHead className="text-right">Interaction</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.map(r => {
              const portShown = netOfFees ? r.portRetNet : r.portRet;
              const activeShown = netOfFees ? r.activeNet : r.active;
              return (
              <>
                <TableRow
                  key={r.level}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => setExpandedLevel(expandedLevel === r.level ? null : r.level)}
                >
                  <TableCell className="font-medium">{range}{r.level}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {portShown != null ? portShown.toFixed(2) : '—'}
                    {netOfFees && r.portRet != null && <span className="block text-[10px] text-muted-foreground">gross {r.portRet.toFixed(2)}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.benchRet != null ? r.benchRet.toFixed(2) : '—'}</TableCell>
                  <TableCell className={`text-right tabular-nums ${activeShown != null ? (activeShown >= 0 ? 'text-emerald-600' : 'text-destructive') : ''}`}>
                    {activeShown != null ? (activeShown >= 0 ? '+' : '') + activeShown.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs">
                    {r.annualOcf > 0 ? r.annualOcf.toFixed(3) : <span className="text-muted-foreground">—</span>}
                    {r.ocfCoverage < 0.99 && r.annualOcf > 0 && <span className="block text-[10px] text-amber-600">{(r.ocfCoverage * 100).toFixed(0)}% covered</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.allocationEffect != null ? (r.allocationEffect >= 0 ? '+' : '') + r.allocationEffect.toFixed(2) : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.selectionEffect != null ? (r.selectionEffect >= 0 ? '+' : '') + r.selectionEffect.toFixed(2) : '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.interactionEffect != null ? (r.interactionEffect >= 0 ? '+' : '') + r.interactionEffect.toFixed(2) : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {r.mode === 'full-brinson' ? 'Brinson-Fachler' : r.mode === 'allocation-only' ? 'Alloc only' : 'Residual'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{expandedLevel === r.level ? 'Hide' : 'Show'} detail</Badge>
                  </TableCell>
                </TableRow>
                {expandedLevel === r.level && (
                  <TableRow key={`${r.level}-detail`}>
                    <TableCell colSpan={10} className="bg-muted/30 p-0">
                      <div className="p-3 space-y-4">
                        <div>
                          <p className="text-xs font-medium mb-2">Sector contributions</p>
                          <div className="max-h-72 overflow-y-auto border rounded bg-background">
                            <Table>
                              <TableHeader className="sticky top-0 bg-background">
                                <TableRow>
                                  <TableHead>Sector</TableHead>
                                  <TableHead className="text-right">wp</TableHead>
                                  <TableHead className="text-right">wb</TableHead>
                                  <TableHead className="text-right">Rb_s</TableHead>
                                  <TableHead className="text-right">Rp_s</TableHead>
                                  <TableHead className="text-right">Alloc</TableHead>
                                  <TableHead className="text-right">Selection</TableHead>
                                  <TableHead className="text-right">Interaction</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sectorRows.map(sr => (
                                  <TableRow key={sr.sector}>
                                    <TableCell className="text-xs">{sr.sector}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs">{sr.wp.toFixed(2)}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{sr.wb.toFixed(2)}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs">{sr.rb != null ? `${sr.rb >= 0 ? '+' : ''}${sr.rb.toFixed(2)}` : <span className="text-muted-foreground italic">n/a</span>}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs">{sr.rp != null ? `${sr.rp >= 0 ? '+' : ''}${sr.rp.toFixed(2)}` : <span className="text-muted-foreground italic">n/a</span>}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs ${sr.allocC >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>{(sr.allocC >= 0 ? '+' : '') + sr.allocC.toFixed(3)}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs ${sr.selC >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>{(sr.selC >= 0 ? '+' : '') + sr.selC.toFixed(3)}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs ${sr.interC >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>{(sr.interC >= 0 ? '+' : '') + sr.interC.toFixed(3)}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs font-medium ${sr.totalC >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>{(sr.totalC >= 0 ? '+' : '') + sr.totalC.toFixed(3)}</TableCell>
                                  </TableRow>
                                ))}
                                {sectorRows.length === 0 && (
                                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground text-xs py-4">No sector weights for this portfolio.</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-medium mb-2">Top holding contributors</p>
                          <div className="max-h-72 overflow-y-auto border rounded bg-background">
                            <Table>
                              <TableHeader className="sticky top-0 bg-background">
                                <TableRow>
                                  <TableHead>Holding</TableHead>
                                  <TableHead>Fund House</TableHead>
                                  <TableHead>Fund Class</TableHead>
                                  <TableHead>ISIN</TableHead>
                                  <TableHead className="text-right">Weight (%)</TableHead>
                                  <TableHead className="text-right">Period return (%)</TableHead>
                                  <TableHead className="text-right">Contribution (pp)</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {holdingRows.slice(0, 25).map(h => (
                                  <TableRow key={h.isin + h.name}>
                                    <TableCell className="text-xs">{h.name}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{h.manager ?? '—'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground">{h.cls ?? '—'}</TableCell>
                                    <TableCell className="text-xs text-muted-foreground font-mono">{h.isin}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs">{h.weight.toFixed(2)}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs ${h.hasData ? (h.ret! >= 0 ? 'text-emerald-600' : 'text-destructive') : 'text-muted-foreground italic'}`}>
                                      {h.hasData ? `${h.ret! >= 0 ? '+' : ''}${h.ret!.toFixed(2)}` : 'n/a'}
                                    </TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs font-medium ${h.contribution != null ? (h.contribution >= 0 ? 'text-emerald-600' : 'text-destructive') : 'text-muted-foreground'}`}>
                                      {h.contribution != null ? `${h.contribution >= 0 ? '+' : ''}${h.contribution.toFixed(3)}` : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {holdingRows.length === 0 && (
                                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground text-xs py-4">No holdings.</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Contribution = (weight / 100) × period return. Holdings missing daily values appear as n/a.
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-medium mb-2">Sleeve breakdown (IA sectors)</p>
                          <div className="max-h-72 overflow-y-auto border rounded bg-background">
                            <Table>
                              <TableHeader className="sticky top-0 bg-background">
                                <TableRow>
                                  <TableHead>IA Sector</TableHead>
                                  <TableHead className="text-right">Weight (%)</TableHead>
                                  <TableHead className="text-right">Period return (%)</TableHead>
                                  <TableHead className="text-right">Contribution (pp)</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sleeveRows.rows.map(s => (
                                  <TableRow key={s.id}>
                                    <TableCell className="text-xs">{s.name}</TableCell>
                                    <TableCell className="text-right tabular-nums text-xs">{s.weight.toFixed(2)}</TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs ${s.ret != null ? (s.ret >= 0 ? 'text-emerald-600' : 'text-destructive') : 'text-muted-foreground italic'}`}>
                                      {s.ret != null ? `${s.ret >= 0 ? '+' : ''}${s.ret.toFixed(2)}` : 'n/a'}
                                    </TableCell>
                                    <TableCell className={`text-right tabular-nums text-xs font-medium ${s.contribution != null ? (s.contribution >= 0 ? 'text-emerald-600' : 'text-destructive') : 'text-muted-foreground'}`}>
                                      {s.contribution != null ? `${s.contribution >= 0 ? '+' : ''}${s.contribution.toFixed(3)}` : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                {sleeveRows.rows.length === 0 && (
                                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-xs py-4">No IA sleeve mappings.</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Sleeve weight = Σ (fund weight × IA mapping weighting). Returns from <code>risk_ia_daily_values</code>.
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
